import { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import Lenis from "lenis";
import Seo from "@/components/Seo";
import { DiamondMark, Grain, MonoLabel } from "@/components/legal";

const LAST_UPDATED = "September 9, 2026";
const SUPPORT_EMAIL = "support@blackdiamondfacility.ca";
const PRIVACY_EMAIL = "privacy@blackdiamondfacility.ca";

const CHAPTERS = [
  {
    id: "agreement",
    num: "01",
    title: "Agreement to these terms",
    blocks: [
      {
        type: "p",
        text: "These Terms of Service govern your use of the Black Diamond mobile app and the website blackdiamondfacility.ca (together, the \u201cService\u201d), operated by BLACK DIAMOND FACILITY SERVICES (\u201cBlack Diamond\u201d, \u201cwe\u201d, \u201cus\u201d). By creating an account or requesting a booking, you agree to these terms. If you do not agree, please do not use the Service.",
      },
      {
        type: "p",
        text: "How we handle your personal information is described in our Privacy Policy at blackdiamondfacility.ca/privacy, which forms part of these terms.",
      },
    ],
  },
  {
    id: "the-service",
    num: "02",
    title: "The Service",
    blocks: [
      {
        type: "p",
        text: "Black Diamond lets you request and manage facility-service bookings for your properties, save property details for faster repeat bookings, and receive booking confirmations and updates by email and push notification.",
      },
      {
        type: "p",
        text: "You can request a booking with or without an account. Some features \u2014 saved properties, booking history, data export \u2014 require an account.",
      },
    ],
  },
  {
    id: "accounts",
    num: "03",
    title: "Accounts and sign-in",
    blocks: [
      {
        type: "list",
        items: [
          "Accounts are created exclusively through Google Sign-In or Sign in with Apple. We never see or store your Google or Apple password.",
          "You agree to provide accurate information and to keep it up to date.",
          "You are responsible for activity that happens under your account and for keeping your signed-in devices secure.",
          "Sign-in sessions expire after 7 days, after which you will need to sign in again.",
        ],
      },
    ],
  },
  {
    id: "bookings",
    num: "04",
    title: "Bookings",
    blocks: [
      {
        type: "p",
        text: "A booking request is not a confirmed appointment. Bookings are subject to availability, service-area eligibility, and our confirmation \u2014 we will confirm or decline by email, and you can follow status changes in the app.",
      },
      {
        type: "list",
        items: [
          "You agree to provide accurate property details, access instructions, and contact information so the service can be performed safely and on time.",
          "You may attach up to eight photos per booking to help us understand the work.",
          "We may decline or cancel a booking, for example when an address is outside our service area or details are incomplete; if that happens we will tell you.",
          "Rush requests are flagged for faster handling but are not guaranteed.",
        ],
      },
    ],
  },
  {
    id: "your-content",
    num: "05",
    title: "Your content",
    blocks: [
      {
        type: "p",
        text: "Photos, notes, listing links, and other content you submit remain yours. You give us the right to store and use that content to operate the Service \u2014 for example, to show your booking details to you and to our team so the work can be done.",
      },
      {
        type: "p",
        text: "You confirm that you have the right to share what you submit: that photos and listing links are yours or used with permission, and that you do not submit other people\u2019s personal information without their consent. We may remove content that violates these terms.",
      },
    ],
  },
  {
    id: "acceptable-use",
    num: "06",
    title: "Acceptable use",
    blocks: [
      {
        type: "p",
        text: "You agree not to misuse the Service. That includes:",
      },
      {
        type: "list",
        items: [
          "No unlawful activity, fraud, or misrepresentation",
          "No automated abuse, scraping, or spam \u2014 the Service enforces rate limits and anti-bot measures",
          "No attempts to interfere with, probe, or disrupt the Service or its security",
          "No uploading malicious files disguised as photos \u2014 uploads are validated by file type",
        ],
      },
    ],
  },
  {
    id: "third-party-services",
    num: "07",
    title: "Third-party services",
    blocks: [
      {
        type: "p",
        text: "The Service relies on Google Sign-In, Sign in with Apple, and the Google Places API for address autocomplete. Your use of those services is governed by Google\u2019s and Apple\u2019s own terms and privacy policies, and we are not responsible for their availability or conduct.",
      },
    ],
  },
  {
    id: "intellectual-property",
    num: "08",
    title: "Intellectual property",
    blocks: [
      {
        type: "p",
        text: "The Service \u2014 including the Black Diamond name, the diamond mark, the app\u2019s design, and its text \u2014 belongs to us or our licensors. You may not copy, reproduce, or reuse any part of it without our written permission, except as needed to use the Service as intended.",
      },
    ],
  },
  {
    id: "disclaimers-and-liability",
    num: "09",
    title: "Disclaimers and liability",
    blocks: [
      {
        type: "p",
        text: "The Service is provided \u201cas is\u201d and \u201cas available\u201d. We work hard to keep it reliable, but we do not guarantee uninterrupted availability, and booking confirmations depend on email and push delivery working as expected.",
      },
      {
        type: "p",
        text: "To the maximum extent permitted by applicable law, we are not liable for indirect, incidental, or consequential damages arising from your use of the Service. Nothing in these terms excludes liability that cannot be excluded by law.",
      },
      {
        type: "p",
        text: "These terms are governed by the laws of the Province of Ontario and the federal laws of Canada applicable there. Any dispute arising from these terms or the Service will be brought before the courts of Ontario, unless applicable law gives you the right to bring it elsewhere.",
      },
    ],
  },
  {
    id: "ending-your-account",
    num: "10",
    title: "Ending your account",
    blocks: [
      {
        type: "p",
        text: "You can delete your account at any time from within the app \u2014 no emails or waiting required. What happens to your data when you do is described step by step in our Privacy Policy.",
      },
      {
        type: "p",
        text: "We may suspend or end access for accounts that violate these terms, particularly the acceptable-use rules above.",
      },
    ],
  },
  {
    id: "changes",
    num: "11",
    title: "Changes to these terms",
    blocks: [
      {
        type: "p",
        text: "We may update these terms from time to time. The current version will always be posted at blackdiamondfacility.ca/terms with its \u201cLast updated\u201d date, and continued use of the Service after an update means you accept the revised terms.",
      },
    ],
  },
  {
    id: "contact",
    num: "12",
    title: "Contact",
    blocks: [
      {
        type: "p",
        text: "The Service is operated by BLACK DIAMOND FACILITY SERVICES. Questions about these terms: support@blackdiamondfacility.ca. For anything about your personal information \u2014 access, correction, export, or deletion \u2014 contact privacy@blackdiamondfacility.ca or see our Privacy Policy.",
      },
    ],
  },
];

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), {
    stiffness: 50,
    damping: 16,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), {
    stiffness: 50,
    damping: 16,
  });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const gemY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const gemSpin = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const line = (text, i, italic = false, testId) => (
    <div key={text} className="overflow-hidden py-[0.5vw]">
      <motion.span
        data-testid={testId}
        className={`block font-serif text-[16vw] leading-[0.95] text-[#F2F2F7] sm:text-[13vw] lg:text-[10.5vw] ${
          italic ? "italic text-[#A1A1A6]" : ""
        }`}
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.15, ease: EASE, delay: 0.25 + i * 0.14 }}
      >
        {text}
      </motion.span>
    </div>
  );

  return (
    <header
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-[92vh] flex-col justify-end overflow-hidden px-6 pb-16 pt-32 sm:px-10 lg:px-16"
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute right-[4%] top-[16%] opacity-90 sm:right-[10%]"
        style={{
          y: gemY,
          rotate: gemSpin,
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          perspective: 800,
        }}
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, ease: EASE, delay: 0.9 }}
      >
        <DiamondMark size={190} />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.15 }}
          className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2"
        >
          <MonoLabel data-testid="terms-hero-kicker">
            Black Diamond — Facility Service
          </MonoLabel>
          <span className="hidden h-px w-16 bg-[#2C2C2E] sm:block" />
          <MonoLabel>Legal Document</MonoLabel>
        </motion.div>

        <h1 className="m-0">
          {line("Terms of", 0, false, "terms-hero-title-line-1")}
          {line("Service", 1, true, "terms-hero-title-line-2")}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.85 }}
          className="mt-10 flex flex-col gap-3 border-t border-[#2C2C2E] pt-6 sm:flex-row sm:items-end sm:justify-between"
        >
          <p
            data-testid="terms-hero-scope"
            className="max-w-md text-sm leading-relaxed text-[#A1A1A6]"
          >
            The terms that govern your use of the Black Diamond app and website
            — written in plain language, matched to what the app actually does.
          </p>
          <MonoLabel data-testid="terms-hero-last-updated">
            Last updated — {LAST_UPDATED}
          </MonoLabel>
        </motion.div>
      </motion.div>
    </header>
  );
};

const MARQUEE_ITEMS = [
  "No hidden fees",
  "Your content stays yours",
  "Delete anytime",
  "Fair terms",
  "No lock-in",
  "Clean work, reliable service",
];

const Marquee = () => (
  <div
    data-testid="terms-editorial-marquee"
    className="overflow-hidden border-y border-[#2C2C2E] py-5"
    aria-hidden="true"
  >
    <div className="marquee-track flex w-max items-center">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex shrink-0 items-center">
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${copy}-${item}`} className="flex items-center">
              <span className="px-8 font-serif text-2xl italic text-[#636366] sm:text-3xl">
                {item}
              </span>
              <span className="inline-block h-2 w-2 rotate-45 bg-[#3A3A3C]" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Block = ({ block }) => {
  if (block.type === "sub") {
    return (
      <h3 className="mt-10 font-serif text-xl text-[#F2F2F7]">{block.text}</h3>
    );
  }
  if (block.type === "callout") {
    return (
      <div className="mt-10 border-l-2 border-[#F2F2F7] bg-[#0C0C0C] px-6 py-5">
        <p className="m-0 text-sm leading-relaxed text-[#F2F2F7]">
          {block.text}
        </p>
      </div>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="m-0 mt-6 list-none space-y-4 p-0">
        {block.items.map((item) => (
          <li key={item.slice(0, 40)} className="flex gap-4">
            <span
              aria-hidden="true"
              className="mt-[9px] h-1.5 w-1.5 shrink-0 rotate-45 bg-[#8E8E93]"
            />
            <span className="text-sm leading-relaxed text-[#A1A1A6]">
              {item}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="m-0 mt-6 text-sm leading-relaxed text-[#A1A1A6] first:mt-0 sm:text-base">
      {block.text}
    </p>
  );
};

const Chapter = ({ chapter }) => (
  <motion.section
    id={chapter.id}
    data-testid={`terms-chapter-${chapter.id}`}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-90px" }}
    transition={{ duration: 0.9, ease: EASE }}
    className="relative scroll-mt-28 border-t border-[#1C1C1E] py-20 sm:py-28"
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -top-6 right-0 select-none font-serif text-[26vw] leading-none text-[#0C0C0C] sm:text-[18vw] lg:text-[13rem]"
    >
      {chapter.num}
    </span>
    <div className="relative max-w-2xl">
      <MonoLabel>Chapter {chapter.num}</MonoLabel>
      <h2 className="mb-8 mt-3 font-serif text-3xl text-[#F2F2F7] sm:text-4xl">
        {chapter.title}
      </h2>
      {chapter.blocks.map((block, i) => (
        <Block key={`${chapter.id}-${i}`} block={block} />
      ))}
    </div>
  </motion.section>
);

const ChapterRail = ({ onNavigate }) => (
  <nav
    data-testid="terms-chapter-rail"
    aria-label="Chapters"
    className="sticky top-28 hidden self-start lg:block"
  >
    <MonoLabel className="mb-6 block">Index</MonoLabel>
    <ul className="m-0 list-none space-y-3 p-0">
      {CHAPTERS.map((c) => (
        <li key={c.id}>
          <a
            data-testid={`terms-chapter-link-${c.id}`}
            href={`#${c.id}`}
            onClick={(e) => onNavigate(e, c.id)}
            className="group flex items-baseline gap-3 text-[#636366] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            <span className="font-mono text-[10px] tracking-[0.2em]">
              {c.num}
            </span>
            <span className="text-[13px]">{c.title}</span>
            <span className="h-px w-0 bg-[#F2F2F7] transition-all duration-300 group-hover:w-6" />
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

export default function Terms() {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  const scrollToChapter = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    const lenis = lenisRef.current;
    if (!el || !lenis) return;
    lenis.resize();
    const y = el.getBoundingClientRect().top + window.scrollY - 96;
    lenis.scrollTo(y, { duration: 1.4, force: true });
  };

  return (
    <div
      data-testid="terms-page"
      className="min-h-screen bg-[#050505] text-[#F2F2F7] antialiased"
      style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}
    >
      <Seo
        title="Terms of Service — Black Diamond"
        siteName="Black Diamond"
        description="The terms governing the Black Diamond facility-service app: accounts, bookings, your content, acceptable use, and how to reach us."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Black Diamond",
          url: "https://blackdiamondfacility.ca",
          email: SUPPORT_EMAIL,
        }}
      />
      <Grain />

      <nav
        data-testid="terms-nav"
        className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-[#1C1C1E] bg-[#050505]/80 px-6 py-4 backdrop-blur-md sm:px-10 lg:px-16"
      >
        <a
          data-testid="terms-nav-brand-link"
          href="https://blackdiamondfacility.ca"
          className="group flex items-center gap-3"
        >
          <span className="transition-transform duration-500 group-hover:rotate-90">
            <DiamondMark size={26} />
          </span>
          <span className="leading-none">
            <span className="block font-mono text-[11px] tracking-[0.32em] text-[#F2F2F7]">
              BLACK DIAMOND
            </span>
            <span className="mt-1 block font-mono text-[8px] tracking-[0.4em] text-[#636366]">
              FACILITY SERVICE
            </span>
          </span>
        </a>
        <div className="flex items-center gap-6">
          <a
            data-testid="terms-nav-privacy-link"
            href="/privacy"
            className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
          >
            Privacy
          </a>
          <MonoLabel data-testid="terms-nav-updated" className="hidden sm:block">
            {LAST_UPDATED}
          </MonoLabel>
        </div>
      </nav>

      <Hero />
      <Marquee />

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 sm:px-10 lg:grid-cols-[240px_1fr] lg:px-16">
        <div className="pt-20 sm:pt-28">
          <ChapterRail onNavigate={scrollToChapter} />
        </div>
        <div>
          {CHAPTERS.map((c) => (
            <Chapter key={c.id} chapter={c} />
          ))}
        </div>
      </main>

      <footer
        data-testid="terms-footer"
        className="border-t border-[#1C1C1E] px-6 py-24 sm:px-10 lg:px-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <MonoLabel>Questions about these terms</MonoLabel>
          <a
            data-testid="terms-footer-email-link"
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-6 inline-block max-w-full break-all font-serif text-[7.5vw] leading-tight text-[#F2F2F7] underline decoration-[#3A3A3C] decoration-1 underline-offset-8 transition-colors duration-300 hover:decoration-[#F2F2F7] sm:text-5xl"
          >
            {SUPPORT_EMAIL}
          </a>
          <div className="mt-20 flex flex-col gap-4 border-t border-[#1C1C1E] pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <DiamondMark size={22} />
              <MonoLabel>Black Diamond — Facility Service</MonoLabel>
            </div>
            <div className="flex items-center gap-6">
              <a
                data-testid="terms-footer-privacy-link"
                href="/privacy"
                className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E8E93] transition-colors duration-300 hover:text-[#F2F2F7]"
              >
                Privacy Policy
              </a>
              <MonoLabel data-testid="terms-footer-tagline">
                Clean work, reliable service.
              </MonoLabel>
            </div>
          </div>
        </motion.div>
      </footer>
    </div>
  );
}
